import Vue from 'vue';

import { Component, Prop } from 'vue-property-decorator';
import { swiper, swiperSlide, SwiperComponent } from 'vue-awesome-swiper';
import { RecentOrFavoriteOrder, Menu, CustomerProfile } from '@/models';
import { SwiperOptions } from 'swiper';
import { Routes, isGroupOrderRoute } from '@/router';
import { AccountManagementEvent } from '@/services/analytics/events';
import { CountryCode } from '@/store/ui';

const NUM_CARDS_TO_SHOW_INITIALLY = 3;

@Component({
  name: 'CmgRecentAndFavoriteOrdersMini',
  components: {
    swiper,
    swiperSlide,
  },
})
export default class CmgRecentAndFavoriteOrdersMini extends Vue {
  showFavorites = true;

  isLoading = false;

  swiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 15,
  };

  @Prop({ required: true, type: Array })
  favorites!: RecentOrFavoriteOrder[];

  @Prop({ required: true, type: Array })
  recents!: RecentOrFavoriteOrder[];

  @Prop({
    required: false,
    default: 'favorites',
    type: String,
    validator: mode => mode == 'recents' || mode == 'favorites',
  })
  mode!: 'recents' | 'favorites';

  get orders(): RecentOrFavoriteOrder[] {
    return (this.showFavorites ? this.favorites : this.recents) || [];
  }

  get visibleOrders(): RecentOrFavoriteOrder[] {
    return this.orders.filter(order => this.isEligibleForReorder(order) || this.showFavorites).slice(0, NUM_CARDS_TO_SHOW_INITIALLY);
  }

  get shouldShowViewAllToggle(): boolean {
    return this.orders.length > NUM_CARDS_TO_SHOW_INITIALLY && !isGroupOrderRoute(this.$route);
  }

  get makeItAFavoriteIconUrl(): string {
    return this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  get showMakeItAFavoriteCard(): boolean {
    // show "Make it a favorite" or "No recent orders?" card appropriately
    return this.showFavorites ? this.visibleOrders.length < 3 : !this.visibleOrders.length;
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get favoritesText(): string {
    return this.$services.config.getFacModalContent()?.favoriteHeader || 'Favorites';
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get favoriteText(): string {
    return this.countryCode === CountryCode.GB ? 'Favourites' : 'Favorites';
  }

  get showFavoritesText(): string {
    return this.showFavorites ? `View All ${this.favoriteText}` : 'View All Recents'
  }

  async mounted() {
    this.showFavorites = this.mode == 'favorites';

    if (this.customer) {
      this.refreshFavorites();
    }
  }

  isFavoriteOrder(order: RecentOrFavoriteOrder): boolean {
    return !!this.favorites.find(o => o.orderId == order.orderId);
  }

  isEligibleForReorder(order: RecentOrFavoriteOrder): boolean {
    return this.$services.recentAndFavoriteOrders.isEligibleForReorder(order, this.isEnrolledInLoyalty);
  }

  getfavoriteIconUrl(order: RecentOrFavoriteOrder): string {
    return this.isFavoriteOrder(order) ? this.$appStore.state.cms.images.icons.heartFilled.default : this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  onClickFavoritesToggle() {
    this.showFavorites = true;
    this.goToFirstSwiperSlide();
  }

  async onClickRecentsToggle() {
    this.showFavorites = false;
    this.goToFirstSwiperSlide();

    await this.refreshRecents();
  }

  goToFirstSwiperSlide(): void {
    const recentFavSwiper = this.$refs.recentsFavoritesSwiper as SwiperComponent;

    if (recentFavSwiper.swiper) {
      recentFavSwiper.swiper.slideTo(0);
    }
  }

  goToRecentAndFavoriteOrdersPage() {
    this.$router.push({
      name: Routes.RecentsAndFavorites,
      query: {
        recents: (!this.showFavorites).toString(),
      },
    });
  }

  async onClickFavoriteToggleForOrder(order: RecentOrFavoriteOrder) {
    this.$services.ui.setLastSelectedElement('icon ' + order.orderId + 'home');
    const isFavorite = this.isFavoriteOrder(order);

    if (isFavorite) {
      this.$services.recentAndFavoriteOrders.removeOrderFromFavorites(order.orderId);
      return;
    }

    this.$services.analytics.send(new AccountManagementEvent('orderFavorited', order.order.meals));

    this.$services.ui.setModalVisibility('addFavoriteOrder', true, { order });
    this.$services.ui.firstFocus();
  }

  async refreshRecents() {
    this.$services.logger.debug('Refreshing recents');

    this.isLoading = true;
    try {
      await this.$services.recentAndFavoriteOrders.refreshRecentOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing recent orders', undefined, e);
    } finally {
      this.isLoading = false;
    }
  }

  async refreshFavorites() {
    this.$services.logger.debug('Refreshing favorites');

    this.isLoading = true;
    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.isLoading = false;
    }
  }
}
